import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getReviewPendingNavEntriesForFacility } from "../../../constants/reviewPendingNavConfig";
import {
  isExceptionFlowForUrologyOrg,
  OrganizationType,
} from "../../../enums/organizationTypes";
import { RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { swapUnderscoreHyphen } from "../../../utils/swapUnderscoreWithHypen";

/**
 * Per-organization map of auth_status IDs that must NOT be rendered as
 * dynamic side-nav entries.
 *
 * Use this whenever an org has a dedicated static side-nav entry (or a
 * different worklist surface) that shadows a dynamic auth-status tab —
 * the duplicate would otherwise show up because `authStatusOptions`
 * still contains it. Orgs in `exceptionFlowForUrologyOrgs` render a static
 * "Orders to Process" entry, so the dynamic `orders_to_process` tab is
 * suppressed here.
 *
 * Keyed by `OrganizationType` (Firebase org doc IDs); values are sets of
 * raw auth_status IDs (underscored form, before `swapUnderscoreHyphen`).
 */
const HIDDEN_AUTH_STATUS_IDS_BY_ORG: Partial<
  Record<OrganizationType, ReadonlySet<string>>
> = {
  [OrganizationType.SUNSTATE_UROLOGY]: new Set<string>(["orders_to_process"]),
};

/**
 * Returns the set of auth_status IDs to omit from the side nav for the
 * given organization. Falls back to an empty set when the org has no
 * configured overrides.
 *
 * @param orgId - The organization ID (Firebase doc ID) for the active session
 * @returns Set of underscored auth_status IDs that must be hidden
 */
const getHiddenAuthStatusIdsForOrg = (orgId: string): ReadonlySet<string> =>
  HIDDEN_AUTH_STATUS_IDS_BY_ORG[orgId as OrganizationType] ?? new Set<string>();

/**
 * Custom hook that generates and manages the side navigation elements for the worklists page.
 *
 * This hook combines static navigation items ("All PA Orders", optional org-specific
 * entries such as "Orders to Process", "My PA Orders", and any review-pending entries)
 * with dynamic auth status options fetched from the Redux store. Dynamic options are
 * sorted by their `left_pane_display_order` and filtered through the per-org hidden
 * auth_status overrides.
 *
 * @returns {Object} An object containing:
 * @returns {Array<{id: string, label: string}>} sideLinks - Array of navigation link objects with `id` and `label` properties.
 * @returns {string} activeTab - The currently active tab ID.
 * @returns {React.Dispatch<React.SetStateAction<string>>} setActiveTab - Function to update the active tab.
 *
 * @example
 * const { sideLinks, activeTab, setActiveTab } = useSideNavElements();
 *
 * // sideLinks will contain:
 * // [
 * //   { id: "medical-pa-orders", label: "All PA Orders" },
 * //   { id: "my-pa-orders", label: "My PA Orders" },
 * //   ...dynamicAuthStatusOptions,
 * // ]
 */
export const useSideNavElements = () => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );
  const [sideLinks, setSideLinks] = useState<{ id: string; label: string }[]>(
    [],
  );
  const [activeTab, setActiveTab] = useState<string>("");
  const orgId = getOrgIdForFetchExternalWorklist();

  const sideNavElements = useMemo(() => {
    const baseElements: { id: string; label: string }[] = [
      { id: "medical-pa-orders", label: "All PA Orders" },
      ...(isExceptionFlowForUrologyOrg(orgId)
        ? [
            { id: "orders-to-process", label: "Orders to Process" },
            /** Drift board for re-ingested orders with changed monitored fields */
            { id: "quick-review", label: "Quick Review" },
          ]
        : []),
      { id: "all-open-orders", label: "All Open Orders" },
      { id: "my-pa-orders", label: "My PA Orders" },
    ];

    const reviewPendingEntries = getReviewPendingNavEntriesForFacility(
      singleHealthFacilityDetails,
    );
    baseElements.push(
      ...reviewPendingEntries.map((e) => ({ id: e.id, label: e.label })),
    );

    return baseElements;
  }, [singleHealthFacilityDetails, orgId]);

  const generateAuthStatusOptionsSortedForUser = useMemo(() => {
    const hiddenAuthStatusIds = getHiddenAuthStatusIdsForOrg(orgId);
    const authStatusOptionsWithLeftPaneDisplayOrder = [
      ...(authStatusOptions ?? []),
    ].sort(
      (a, b) =>
        (a?.left_pane_display_order ?? 999) -
        (b?.left_pane_display_order ?? 999),
    );
    const dynamicAuthOptions =
      authStatusOptionsWithLeftPaneDisplayOrder
        ?.filter((option) => !hiddenAuthStatusIds.has(option.id))
        .map((option) => ({
          id: swapUnderscoreHyphen(option.id),
          label: option.text,
        })) || [];

    const combinedSideLinks = [...sideNavElements, ...dynamicAuthOptions];
    return combinedSideLinks;
  }, [authStatusOptions, sideNavElements, orgId]);

  useEffect(() => {
    setSideLinks(generateAuthStatusOptionsSortedForUser);
    if (
      generateAuthStatusOptionsSortedForUser &&
      generateAuthStatusOptionsSortedForUser.length > 0
    ) {
      setActiveTab(generateAuthStatusOptionsSortedForUser?.[0]?.id ?? "");
    }
  }, [generateAuthStatusOptionsSortedForUser]);
  return { sideLinks, activeTab, setActiveTab };
};
